/**
 * Single method to send a JSON payload to the Make.com webhook.
 * All forms (contact, request-info, lead-capture, art-fair, subscribe, wall-visualizer,
 * art-finder, collector-guide) call sendToMake(payload). We always send the same
 * superset of keys so Make.com exposes every field for mapping. Legacy payload keys
 * are merged into canonical names (e.g. body → notes, utm_campaign → campaign, show → event).
 * `location` is used by collector-guide (homepage | collector-guide-page | …).
 *
 * Returns a Promise that resolves when Make accepts the webhook (HTTP 2xx),
 * and rejects on network/HTTP failure so callers can show real status.
 */
(function () {
  var SUPERSET_KEYS = [
    'form_type', 'create_date', 'last_update',
    'first_name', 'last_name', 'email', 'phone', 'notes',
    'updates', 'source', 'event', 'location',
    'question_about_artwork', 'sms_opt_in', 'interest',
    'budget', 'zipcode',
    'size_range', 'timeline', 'room_placement', 'product_title', 'product_url',
    'utm_source', 'campaign', 'tags',
    'art_finder_profile', 'art_finder_intent', 'art_finder_medium', 'art_finder_handles'
  ];

  function valueForKey(payload, key) {
    var v;
    switch (key) {
      case 'interest':
        v = payload.interest;
        if (v === undefined || v === null || String(v).trim() === '') {
          v = payload.interest_categories;
        }
        break;
      case 'notes':
        v = payload.notes;
        if (v === undefined || v === null || String(v).trim() === '') {
          v = payload.body;
        }
        if (v === undefined || v === null || String(v).trim() === '') {
          v = payload.note;
        }
        break;
      case 'campaign':
        v = payload.campaign;
        if (v === undefined || v === null || String(v).trim() === '') {
          v = payload.utm_campaign;
        }
        break;
      case 'event':
        v = payload.event;
        if (v === undefined || v === null || String(v).trim() === '') {
          v = payload.show;
        }
        break;
      case 'timeline':
        v = payload.timeline;
        if (v === undefined || v === null || String(v).trim() === '') {
          v = payload.purchase_intent;
        }
        break;
      case 'size_range':
        v = payload.size_range;
        if (v === undefined || v === null || String(v).trim() === '') {
          v = payload.preferred_print;
        }
        break;
      case 'budget':
        v = payload.budget;
        if (v === undefined || v === null || String(v).trim() === '') {
          v = payload.budget_range;
        }
        break;
      default:
        v = payload[key];
    }
    if (v === undefined || v === null) return '';
    return String(v);
  }

  window.sendToMake = function (payload, options) {
    options = options || {};
    if (!payload || typeof payload !== 'object') return Promise.resolve({ ok: true, skipped: true });
    if (!window.__makeWebhookUrl) return Promise.resolve({ ok: true, skipped: true, reason: 'no-webhook' });

    var normalized = {};
    for (var i = 0; i < SUPERSET_KEYS.length; i++) {
      var k = SUPERSET_KEYS[i];
      normalized[k] = valueForKey(payload, k);
    }
    // Lead Storage maps create_date/last_update through formatDate — never send blanks.
    if (!normalized.create_date || !normalized.last_update) {
      var nowIso = new Date().toISOString();
      if (!normalized.create_date) normalized.create_date = nowIso;
      if (!normalized.last_update) normalized.last_update = nowIso;
    }
    var jsonBody = JSON.stringify(normalized);

    /**
     * CORS note:
     * - `Content-Type: application/json` triggers a CORS preflight in browsers.
     * - Some webhook endpoints don't handle OPTIONS, causing the POST never to fire.
     * Default: JSON as `text/plain` (simple request). Opt into beacon only when
     * navigating away (options.useBeacon) where a response is not required.
     */
    var headers = {};
    var url = window.__makeWebhookUrl;
    var hasSecret = !!window.__makeWebhookSecret;

    if (hasSecret) {
      headers['Content-Type'] = 'application/json';
      headers['X-Webhook-Secret'] = window.__makeWebhookSecret;
    } else {
      headers['Content-Type'] = 'text/plain;charset=UTF-8';
    }

    if (options.useBeacon && !hasSecret && navigator && typeof navigator.sendBeacon === 'function') {
      try {
        var blob = new Blob([jsonBody], { type: 'text/plain;charset=UTF-8' });
        if (navigator.sendBeacon(url, blob)) {
          return Promise.resolve({ ok: true, beacon: true });
        }
      } catch (e) {}
    }

    return fetch(url, {
      method: 'POST',
      headers: headers,
      body: jsonBody,
      keepalive: true,
      credentials: 'omit'
    }).then(function (res) {
      if (!res.ok) {
        var err = new Error('Make webhook HTTP ' + res.status);
        err.status = res.status;
        throw err;
      }
      return res;
    });
  };
})();
